import React, { ReactElement, useState } from 'react'
import { useSelector } from 'react-redux'

import PropTypes from 'prop-types'
import styled from 'styled-components'

import { PROGRAM_TAXONOMY } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/customerProgram'
import { isImmutable } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/immutableUtils'
import { formatPhoneNumberForDisplay } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/stringUtils'
import { Address, ProviderProgramTaxonomy } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { ButtonSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/baseButton/BaseButton'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import styles from './displayProviderInfo.module.scss'
import { DA_PROGRAM_TAXONOMY_TO_DISPLAY_VALUE, demographicAgeMap } from '../../common/constants/appConstants'
import { getAuthInPersonPreference } from '../../data/auth/authSelectors'
import InPersonToggle from '../../lyraTherapy/clients/InPersonToggle'

const PracticeInformationHelperTextContainer = styled.p({
  paddingBottom: '16px',
  fontStyle: 'italic',
})

const BodyTextContainer = styled.p({ paddingBottom: '8px' })

// Given a provider's treatment modality such as 'individual,' 'lyraTherapy,' or 'singleSessionCoaching,'
// return the corresponding treatment option
const convertProviderTreatmentModalityToTreatmentDisplay = (program: string): string => {
  return DA_PROGRAM_TAXONOMY_TO_DISPLAY_VALUE[program as keyof typeof DA_PROGRAM_TAXONOMY_TO_DISPLAY_VALUE]?.display
}

// parses a provider's program taxonomies and returns array of corresponding provider facing program display names
const retrieveTreatmentDisplayFromProviderProgramTaxonomy = (
  data: ProviderProgramTaxonomy[],
): (string | undefined)[] => {
  return data
    .map((item: ProviderProgramTaxonomy) =>
      item.treatment === PROGRAM_TAXONOMY.treatment.therapy
        ? convertProviderTreatmentModalityToTreatmentDisplay(item.clientele)
        : item.treatment === PROGRAM_TAXONOMY.treatment.medicationManagement
          ? convertProviderTreatmentModalityToTreatmentDisplay(item.treatment)
          : undefined,
    )
    .filter(Boolean) // removes undefined values
}

const convertAgesTreatedToListItems = (ages_treated: { [ageDemo: string]: string[] }) => {
  const listItems: ReactElement[] = []
  if (!ages_treated) return listItems
  Object.keys(ages_treated).forEach((ageDemo: string) => {
    ages_treated[ageDemo].forEach((sessionFormat: string) => {
      listItems.push(
        <li key={ageDemo}>{`${demographicAgeMap[ageDemo as keyof typeof demographicAgeMap]} (${sessionFormat})`}</li>,
      )
    })
  })
  return listItems
}

const convertAddressObjectToString = (address: Address) => {
  return `${address.street1}, ${address.city}, ${address.state}. ${address.zipcode}`
}

const DisplayProviderInfo = ({
  $$data,
  showSummaryAndSpecialties,
}: {
  $$data: any
  showSummaryAndSpecialties?: boolean
}) => {
  const data = isImmutable({ value: $$data }) ? $$data.toJS() : $$data
  const myInfoAddresses: Address[] | undefined = data.addresses
  const inPersonPreference: boolean = useSelector(getAuthInPersonPreference) ?? false
  const [showInPersonSuccess, setShowInPersonSuccess] = useState(false)
  return (
    <div>
      <div className={styles.section}>
        <h3>Personal Info</h3>

        <div className='row'>
          <div className='col-xs-12 col-sm-6'>
            <div className={styles['field-label']}>First Name</div>
            <p>{data.first_name}</p>
          </div>
          <div className='col-xs-12 col-sm-6'>
            <div className={styles['field-label']}>Last Name</div>
            <p>{data.last_name}</p>
          </div>
        </div>
      </div>

      <div className={styles.section}>
        <h3>Contact Info</h3>

        <div className='row'>
          <div className='col-xs-12 col-sm-6'>
            <div className={styles['field-label']}>Email</div>
            <p>{data.email}</p>
          </div>
          <div className='col-xs-12 col-sm-6'>
            <div className={styles['field-label']}>Practice Phone Number</div>
            <p>{data.phone}</p>
          </div>
        </div>
        <div className='row'>
          <div className='col-xs-12 col-sm-6'>
            {data.intake_representative_email && (
              <>
                <div className={styles['field-label']}>Client Intake Email</div>
                <p>{data.intake_representative_email}</p>
              </>
            )}
          </div>
          <div className='col-xs-12 col-sm-6'>
            {data.intake_representative_phone && (
              <>
                <div className={styles['field-label']}>Client Intake Phone</div>
                <p>{formatPhoneNumberForDisplay(data.intake_representative_phone)}</p>
              </>
            )}
          </div>
        </div>
      </div>

      {showSummaryAndSpecialties && (
        <div className={styles.section}>
          <h3>Practice Information</h3>
          <PracticeInformationHelperTextContainer>
            Information below is visible to Lyra members and may impact how you are matched with members searching for
            care. To update this information,{' '}
            <a
              href='https://provider-support.lyrahealth.com/hc/en-us/requests/new?ticket_form_id=538748'
              target='_blank'
              rel='noreferrer'
            >
              submit a request
            </a>{' '}
            to our support team. If you have recently requested a change to this information, please allow 24 hours for
            these changes to be reflected. To optimize your Lyra Profile,{' '}
            <a
              href='https://provider-support.lyrahealth.com/hc/en-us/articles/10883636435219-How-to-Optimize-Your-Profile-to-Increase-Referrals'
              target='_blank'
              rel='noreferrer'
            >
              visit our Help Center
            </a>
            .
          </PracticeInformationHelperTextContainer>

          <div className='row'>
            <div className='col-lg-12 col-sm-6'>
              <div className={styles['field-label']}>Open to In-Person Care</div>
              <InPersonToggle
                showInPersonSuccess={showInPersonSuccess}
                onPressAfterUpdate={() => setShowInPersonSuccess(true)}
                providerId={data.id}
                testID={tID('DisplayProviderInfo-in-person-button')}
                size={ButtonSize.SMALL}
                isMyInfoPage
                customStyles={styles}
                myInfoAddresses={myInfoAddresses}
              />
              {(showInPersonSuccess || inPersonPreference) && (
                <ul>
                  {myInfoAddresses?.map((address: Address) => (
                    <li key={address.street1}>{convertAddressObjectToString(address)}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className='row'>
            <div className='col-lg-12 col-sm-6'>
              <div className={styles['field-label']}>Summary</div>
              <BodyTextContainer>
                <BodyText text={data.summary} />
              </BodyTextContainer>
            </div>
          </div>

          <div className='row'>
            <div className='col-lg-12 col-sm-6'>
              <div className={styles['field-label']}>Conditions Treated</div>
              <ul>
                {data.conditions?.map((condition: string) => (
                  <li key={condition}>{condition}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className='row'>
            {data.negative_conditions && (
              <div className='col-xs-12 col-sm-6'>
                <div className={styles['field-label']}>Excluded Conditions</div>
                <ul>
                  {data.negative_conditions?.map((condition: string) => (
                    <li key={condition}>{condition}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className='col-xs-12 col-sm-6'>
              <div className={styles['field-label']}>Ages Treated</div>
              <ul>{convertAgesTreatedToListItems(data.ages_treated)}</ul>
            </div>
            <div className='col-xs-12 col-sm-6'>
              <div className={styles['field-label']}>Contracted Services</div>
              <ul>
                {retrieveTreatmentDisplayFromProviderProgramTaxonomy(data.program_taxonomies)?.map(
                  (condition: string) => (
                    <li key={condition}>{condition}</li>
                  ),
                )}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

DisplayProviderInfo.propTypes = {
  $$data: PropTypes.object,
}

export default DisplayProviderInfo
